import { useAuth } from "@/components/auth/AuthProvider";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

const MyReviews = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data: reviews, isLoading } = useQuery({
    queryKey: ["userReviews", user?.id],
    queryFn: async () => {
      console.log("Fetching reviews for user:", user?.id);
      const { data, error } = await supabase
        .from("reviews")
        .select(`
          *,
          products (
            id,
            name,
            brand,
            image_url
          )
        `)
        .eq("user_id", user?.id)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching reviews:", error);
        throw error;
      }
      
      console.log("Fetched reviews:", data);
      return data;
    },
    enabled: !!user?.id,
  });

  if (isLoading) {
    return <div>{t("common.loading")}</div>;
  }

  if (!reviews?.length) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500 mb-4">{t("profile.noReviews")}</p>
        <Button onClick={() => navigate("/")}>
          {t("profile.exploreProducts")}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {reviews.map((review) => (
        <div
          key={review.id}
          className="flex gap-4 p-4 border rounded-lg hover:bg-gray-50 transition-colors"
        >
          <div className="flex-shrink-0">
            <Avatar className="h-12 w-12">
              <AvatarImage src={review.products.image_url} alt={review.products.name} />
              <AvatarFallback>{review.products.brand[0]}</AvatarFallback>
            </Avatar>
          </div>
          <div className="flex-grow">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-medium hover:text-blue-600 cursor-pointer" onClick={() => navigate(`/product/${review.products.id}`)}>
                  {review.products.name}
                </h3>
                <p className="text-sm text-gray-500">{review.products.brand}</p>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-yellow-500">★</span>
                <span>{review.rating}</span>
              </div>
            </div>
            <p className="mt-2 text-gray-700">{review.comment}</p>
            <div className="mt-2 text-sm text-gray-500">
              {new Date(review.created_at).toLocaleDateString()}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default MyReviews;